import { useParams } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import {
  useRestaurantEdgeSettings,
  useSaveRestaurantEdgeSettings,
} from '../hooks/use-restaurant-edge-settings'
import { KiosksTab } from '../components/edge-settings/edge-settings-kiosks-tab'
import { DevicesTab } from '../components/edge-settings/edge-settings-devices-tab'
import { RoutingTab } from '../components/edge-settings/edge-settings-routing-tab'
import { SettingsTab } from '../components/edge-settings/edge-settings-settings-tab'
import {
  DEFAULT_KIOSK,
  DEFAULT_OPS,
  DEFAULT_PAYMENT,
  DEFAULT_PRINTER,
  DEFAULT_ROUTING,
  EMPTY_DEVICE,
  asBoolean,
  asNumber,
  asRecord,
  asString,
  findSetting,
  normalizeKiosksForSave,
  normalizeRoutingForSave,
  parseDeviceRouting,
  parseDevices,
  parseKiosks,
} from '../components/edge-settings/edge-settings-utils'
import type {
  EdgeDeviceConfig,
  EdgeDeviceRoutingSettings,
  EdgeKioskConfig,
  RestaurantEdgeSettings,
  RestaurantEdgeSettingsPatch,
} from '@/api/types'
import type {
  OpsBackups,
  PaymentCardDefaults,
  PrinterDefaults,
} from '../components/edge-settings/edge-settings-utils'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'

export function RestaurantEdgeSettingsPage() {
  const { t: tRestaurants } = useTranslation('restaurants')
  const { id } = useParams({ strict: false })
  const restaurantId = Number(id)
  const { data, isLoading } = useRestaurantEdgeSettings(restaurantId)
  const save = useSaveRestaurantEdgeSettings(restaurantId)

  const [kiosks, setKiosks] = useState<Array<EdgeKioskConfig>>([DEFAULT_KIOSK])
  const [printer, setPrinter] = useState<PrinterDefaults>(DEFAULT_PRINTER)
  const [payment, setPayment] = useState<PaymentCardDefaults>(DEFAULT_PAYMENT)
  const [ops, setOps] = useState<OpsBackups>(DEFAULT_OPS)
  const [devices, setDevices] = useState<Array<EdgeDeviceConfig>>([])
  const [deviceRouting, setDeviceRouting] =
    useState<Required<EdgeDeviceRoutingSettings>>(DEFAULT_ROUTING)
  const [initialDeviceRouting, setInitialDeviceRouting] =
    useState<Required<EdgeDeviceRoutingSettings>>(DEFAULT_ROUTING)
  const [initialDeviceIds, setInitialDeviceIds] = useState<Array<string>>([])

  useEffect(() => {
    if (!data) return
    const settings = data.settings
    const kiosksEntry = findSetting(settings, 'kiosks')?.value
    const parsedKiosks = parseKiosks(kiosksEntry)
    let effectiveKiosks = parsedKiosks
    if (parsedKiosks.length > 0) {
      setKiosks(parsedKiosks)
    } else {
      const kioskEntry = asRecord(findSetting(settings, 'kiosk')?.value)
      effectiveKiosks = [
        {
          stationId: asString(kioskEntry.stationId, DEFAULT_KIOSK.stationId),
          zone: asString(kioskEntry.zone, DEFAULT_KIOSK.zone),
          name: asString(kioskEntry.name, DEFAULT_KIOSK.name || ''),
        },
      ]
      setKiosks(effectiveKiosks)
    }

    const printerEntry = asRecord(
      findSetting(settings, 'printer.defaults')?.value,
    )
    setPrinter({
      codePage: asString(printerEntry.codePage, DEFAULT_PRINTER.codePage),
      qrEnabled: asBoolean(printerEntry.qrEnabled, DEFAULT_PRINTER.qrEnabled),
      qrSize: asNumber(printerEntry.qrSize, DEFAULT_PRINTER.qrSize),
      qrEcLevel: asString(printerEntry.qrEcLevel, DEFAULT_PRINTER.qrEcLevel),
    })

    const paymentEntry = asRecord(findSetting(settings, 'payment.card')?.value)
    const zvtDefaults = asRecord(paymentEntry.zvtDefaults)
    const hobexEcr = asRecord(paymentEntry.hobexEcr)
    setPayment({
      zvtDefaults: {
        timeout3Ms: asNumber(
          zvtDefaults.timeout3Ms,
          DEFAULT_PAYMENT.zvtDefaults.timeout3Ms,
        ),
        timeout4Ms: asNumber(
          zvtDefaults.timeout4Ms,
          DEFAULT_PAYMENT.zvtDefaults.timeout4Ms,
        ),
        configByte: asString(
          zvtDefaults.configByte,
          DEFAULT_PAYMENT.zvtDefaults.configByte,
        ),
        cardReceiptPrint: asString(
          zvtDefaults.cardReceiptPrint,
          DEFAULT_PAYMENT.zvtDefaults.cardReceiptPrint,
        ),
        language: asString(
          zvtDefaults.language,
          DEFAULT_PAYMENT.zvtDefaults.language,
        ),
        forceRegistration: asBoolean(
          zvtDefaults.forceRegistration,
          DEFAULT_PAYMENT.zvtDefaults.forceRegistration,
        ),
        delayBetweenCommandsMs: asNumber(
          zvtDefaults.delayBetweenCommandsMs,
          DEFAULT_PAYMENT.zvtDefaults.delayBetweenCommandsMs,
        ),
      },
      autoLogonOnStartup: asBoolean(
        paymentEntry.autoLogonOnStartup,
        DEFAULT_PAYMENT.autoLogonOnStartup,
      ),
      healthPollSeconds: asNumber(
        paymentEntry.healthPollSeconds,
        DEFAULT_PAYMENT.healthPollSeconds,
      ),
      healthGateRouting: asBoolean(
        paymentEntry.healthGateRouting,
        DEFAULT_PAYMENT.healthGateRouting,
      ),
      useStub: asBoolean(paymentEntry.useStub, DEFAULT_PAYMENT.useStub),
      hobexEcr: {
        timeoutMs: asNumber(
          hobexEcr.timeoutMs,
          DEFAULT_PAYMENT.hobexEcr.timeoutMs,
        ),
      },
    })

    const opsEntry = asRecord(findSetting(settings, 'ops.backups')?.value)
    setOps({
      enabled: asBoolean(opsEntry.enabled, DEFAULT_OPS.enabled),
      dir: asString(opsEntry.dir, DEFAULT_OPS.dir),
      dbPath: asString(opsEntry.dbPath, DEFAULT_OPS.dbPath),
      cron: asString(opsEntry.cron, DEFAULT_OPS.cron),
    })

    const parsedDevices = parseDevices(data.devices)
    setDevices(parsedDevices)
    const routingEntry = findSetting(settings, 'device.routing')?.value
    const parsedRouting = parseDeviceRouting(routingEntry)
    const normalizedRouting = normalizeRoutingForSave(
      parsedRouting,
      normalizeKiosksForSave(effectiveKiosks),
      parsedDevices,
    )
    setDeviceRouting(normalizedRouting)
    setInitialDeviceRouting(normalizedRouting)
    setInitialDeviceIds(
      parsedDevices
        .map((dev) => (typeof dev.id === 'string' ? dev.id.trim() : ''))
        .filter((deviceId): deviceId is string => deviceId.length > 0),
    )
  }, [data])

  const normalizedKiosks = useMemo(
    () => normalizeKiosksForSave(kiosks),
    [kiosks],
  )
  const zoneOptions = useMemo(
    () => Array.from(new Set(normalizedKiosks.map((k) => k.zone))),
    [normalizedKiosks],
  )
  const normalizedDeviceRouting = useMemo(
    () => normalizeRoutingForSave(deviceRouting, normalizedKiosks, devices),
    [deviceRouting, normalizedKiosks, devices],
  )
  const settingsPayload = useMemo(() => {
    const primaryKiosk = normalizedKiosks[0]
    const items: RestaurantEdgeSettings['settings'] = [
      { key: 'kiosks', value: normalizedKiosks },
      { key: 'printer.defaults', value: printer },
      { key: 'payment.card', value: payment },
      { key: 'ops.backups', value: ops },
      { key: 'device.routing', value: normalizedDeviceRouting },
    ]
    items.splice(0, 0, { key: 'kiosk', value: primaryKiosk })
    return items
  }, [normalizedKiosks, printer, payment, ops, normalizedDeviceRouting])

  const unsavedDeviceCount = useMemo(
    () =>
      devices.filter(
        (device) =>
          !(typeof device.id === 'string' && device.id.trim().length > 0) &&
          ((device.name || '').trim().length > 0 ||
            (device.role || '').trim().length > 0),
      ).length,
    [devices],
  )

  const missingRequiredRoutingCount = useMemo(() => {
    const routingStationMap = new Map<string, string>()
    for (const item of normalizedDeviceRouting.stationAssignments) {
      routingStationMap.set(`${item.stationId}::${item.role}`, item.deviceId)
    }
    const routingZoneMap = new Map<string, string>()
    for (const item of normalizedDeviceRouting.zoneDefaults) {
      routingZoneMap.set(`${item.zone}::${item.role}`, item.deviceId)
    }
    const routingFallbackMap = new Map<string, string>()
    for (const item of normalizedDeviceRouting.fallbacks) {
      routingFallbackMap.set(item.role, item.deviceId)
    }
    let missing = 0
    for (const kiosk of normalizedKiosks) {
      for (const role of ['payment.card', 'printer.receipt'] as const) {
        const stationKey = `${kiosk.stationId}::${role}`
        const zoneKey = `${kiosk.zone}::${role}`
        const assignedDeviceId =
          routingStationMap.get(stationKey) ??
          routingZoneMap.get(zoneKey) ??
          routingFallbackMap.get(role)
        if (!assignedDeviceId) missing += 1
      }
    }
    return missing
  }, [normalizedKiosks, normalizedDeviceRouting])

  function updateKiosk(index: number, patch: Partial<EdgeKioskConfig>) {
    setKiosks((prev) =>
      prev.map((k, i) => (i === index ? { ...k, ...patch } : k)),
    )
  }

  function addKiosk() {
    setKiosks((prev) => [...prev, { ...DEFAULT_KIOSK }])
  }

  function removeKiosk(index: number) {
    setKiosks((prev) => prev.filter((_, i) => i !== index))
  }

  function updateDevice(index: number, patch: Partial<EdgeDeviceConfig>) {
    setDevices((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    )
  }

  function addDevice() {
    setDevices((prev) => [...prev, { ...EMPTY_DEVICE }])
  }

  function removeDevice(index: number) {
    setDevices((prev) => prev.filter((_, i) => i !== index))
  }

  function normalizeDevicesForSave() {
    const kioskIds = new Set(normalizedKiosks.map((k) => k.stationId))
    return devices
      .map((d) => ({
        id: d.id ? d.id.trim() : undefined,
        name: (d.name || '').trim(),
        role: (d.role || '').trim(),
        protocol: d.protocol ? d.protocol.trim() : undefined,
        host: d.host ? d.host.trim() : undefined,
        port: d.port ?? undefined,
        zones: (d.zones || []).map((z) => z.trim()).filter(Boolean),
        allowedKiosks: (d.allowedKiosks || [])
          .map((k) => k.trim())
          .filter((k) => k.length > 0 && kioskIds.has(k)),
        priority: d.priority ?? undefined,
        settings:
          d.role === 'payment.card' && d.settings?.cardReceiptPrint
            ? { cardReceiptPrint: d.settings.cardReceiptPrint }
            : undefined,
      }))
      .map((d) => ({
        ...d,
        name: d.name ? d.name : undefined,
        protocol: d.protocol ? d.protocol : undefined,
        host: d.host ? d.host : undefined,
        allowedKiosks: d.allowedKiosks.length > 0 ? d.allowedKiosks : undefined,
      }))
      .filter((d) => d.name && d.role)
  }

  function setDeviceKiosks(index: number, next: Array<string>) {
    setDevices((prev) =>
      prev.map((d, i) => {
        if (i !== index) return d
        return { ...d, allowedKiosks: next }
      }),
    )
  }

  function handleSave() {
    if (normalizedKiosks.length === 0) {
      toast.error(tRestaurants('edgeSettings.toasts.kioskMissing'))
      return
    }
    const kioskIds = new Set(normalizedKiosks.map((k) => k.stationId))
    if (kioskIds.size !== normalizedKiosks.length) {
      toast.error(tRestaurants('edgeSettings.toasts.kioskDuplicate'))
      return
    }
    const invalid = devices.find((d) => {
      const hasAny = Boolean(
        d.name ||
        d.role ||
        d.host ||
        d.port ||
        d.protocol ||
        (d.zones && d.zones.length > 0),
      )
      return hasAny && (!(d.name || '').trim() || !(d.role || '').trim())
    })
    if (invalid) {
      toast.error(tRestaurants('edgeSettings.toasts.deviceNameRole'))
      return
    }
    const invalidCashRecycler = devices.find(
      (d) =>
        d.role === 'payment.cash_recycler' &&
        (!d.host?.trim() || !d.port || !Number.isFinite(d.port)),
    )
    if (invalidCashRecycler) {
      toast.error(tRestaurants('edgeSettings.toasts.deviceCashRecyclerMissing'))
      return
    }
    if (missingRequiredRoutingCount > 0) {
      toast.warning(
        tRestaurants('edgeSettings.toasts.routingMissingRequired', {
          count: missingRequiredRoutingCount,
        }),
      )
    }
    const devicesUpsert = normalizeDevicesForSave()
    const nextIds = new Set(
      devicesUpsert
        .map((dev) => (typeof dev.id === 'string' ? dev.id.trim() : ''))
        .filter((deviceId): deviceId is string => deviceId.length > 0),
    )
    const devicesDelete = initialDeviceIds.filter(
      (deviceId) => !nextIds.has(deviceId),
    )
    const payload: RestaurantEdgeSettingsPatch = {
      settingsUpsert: settingsPayload,
      settingsDelete: [],
      devicesUpsert,
      devicesDelete,
    }
    save.mutate(payload, {
      onSuccess: (res) => {
        toast.success(
          tRestaurants('edgeSettings.toasts.saveSuccess', {
            version: res.version,
          }),
        )
      },
      onError: () => {
        toast.error(tRestaurants('edgeSettings.toasts.saveError'))
      },
    })
  }

  return (
    <PermissionGate action={ACTIONS.RESTAURANT_EDGE_SETTINGS_MANAGE}>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">
              {tRestaurants('edgeSettings.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {tRestaurants('edgeSettings.subtitle')}
            </p>
          </div>
          <Button onClick={handleSave} disabled={save.isPending || isLoading}>
            {save.isPending
              ? tRestaurants('edgeSettings.saving')
              : tRestaurants('edgeSettings.save')}
          </Button>
        </div>

        <Tabs defaultValue="kiosks">
          <TabsList>
            <TabsTrigger value="kiosks">
              {tRestaurants('edgeSettings.kiosks.title')}
            </TabsTrigger>
            <TabsTrigger value="devices">
              {tRestaurants('edgeSettings.devices.title')}
            </TabsTrigger>
            <TabsTrigger value="routing">
              {tRestaurants('edgeSettings.routing.title')}
            </TabsTrigger>
            <TabsTrigger value="settings">
              {tRestaurants('edgeSettings.tabs.settings')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="kiosks">
            <KiosksTab
              kiosks={kiosks}
              onUpdateKiosk={updateKiosk}
              onAddKiosk={addKiosk}
              onRemoveKiosk={removeKiosk}
            />
          </TabsContent>

          <TabsContent value="devices">
            <DevicesTab
              devices={devices}
              normalizedKiosks={normalizedKiosks}
              zoneOptions={zoneOptions}
              onUpdateDevice={updateDevice}
              onAddDevice={addDevice}
              onRemoveDevice={removeDevice}
              onSetDeviceKiosks={setDeviceKiosks}
            />
          </TabsContent>

          <TabsContent value="routing">
            <RoutingTab
              normalizedKiosks={normalizedKiosks}
              devices={devices}
              zoneOptions={zoneOptions}
              normalizedDeviceRouting={normalizedDeviceRouting}
              initialDeviceRouting={initialDeviceRouting}
              unsavedDeviceCount={unsavedDeviceCount}
              onSetDeviceRouting={setDeviceRouting}
            />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab
              printer={printer}
              payment={payment}
              ops={ops}
              onSetPrinter={setPrinter}
              onSetPayment={setPayment}
              onSetOps={setOps}
            />
          </TabsContent>
        </Tabs>
      </div>
    </PermissionGate>
  )
}
